import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Rating,
  Typography,  
} from "@mui/material";

import { RootState } from "../../../store/Index";

import { PilotReviewProps } from "../types";
import apiService from "../../../service/Api";
import { Launch } from "@mui/icons-material";

interface ViewReviewDialogProps {
  open: boolean;
  onClose: () => void;
  selectedReview: number | null;
};

export const ViewReviewDialog: React.FC<ViewReviewDialogProps> = ({
  open,
  onClose,
  selectedReview,
}) => {
  const [review, setReview] = useState<PilotReviewProps | null>();
  const [loading, setLoading] = useState(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchReview = async () => {
      setLoading(true);
      try {
        const response = await apiService.get<PilotReviewProps>(
          `/api/v1/crews/crew-ratings/${selectedReview}/`
        );
        setReview(response.data);
      } catch (err) {
        console.error("Failed to fetch pilot review:", err);
      } finally {
        setLoading(false);
      }
    };

    if (selectedReview) {
      fetchReview();
    }
  }, [selectedReview]);

  if (!review) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Box display={'flex'} alignItems={'center'} gap={1}>
          {loading && (
            <CircularProgress sx={{ color: 'primary' }} />
          )}
          <Avatar
            src={review.crew_member_summary.profile_picture || ""}
            sx={{
              width: 32,
              height: 32,
              bgcolor: reduxTheme.primary_colors[800],
              color: reduxTheme.primary_colors[100],
            }}
          />
          <Box sx={{ lineHeight: 1 }}>
            <Typography variant={'body1'} sx={{ fontWeight: 600 }}>
              {review.crew_member_summary.first_name} {review.crew_member_summary.last_name}
            </Typography>
            <Typography variant={'caption'}>{`ID: ${review.crew_member}`}</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display={'flex'} alignItems={'center'} sx={{ mb: 1 }} gap={2}>
          <Typography variant={'body2'}>Feedback by:</Typography>
          <Box display={'flex'} alignItems={'center'} gap={.4}>
            <Typography variant={'body2'} sx={{ fontWeight: 600 }}>
              {review.rater_summary?.first_name} {review.rater_summary?.last_name}
            </Typography>
            <Chip
              label={review.rater_summary?.role || "N/A"}
              size={'small'}
              sx={{
                bgcolor: "#724E41",
                border: '1px solid',
                borderColor: "#724E41",
                fontWeight: 600,
                fontSize: 10,
                textTransform: "uppercase",
                color: "#FFF",
                borderRadius: 1
              }}
            />
          </Box>          
        </Box>

        {review.stream_detail && (
          <Box display={'flex'} alignItems={'center'} sx={{ mb: 1 }} gap={2}>
            <Typography variant={'body2'}>Stream for:</Typography>
            <Box
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  color: reduxTheme.primary_colors[400]
                }
              }}
              onClick={() => navigate(`/stream-management/view/${review.stream_detail?.id}`)}
              display={'flex'}
              alignItems={'center'}
              gap={.4}>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{review.stream_detail.title}</Typography>
                <Launch sx={{ fontSize: 14 }} />
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 2 }} display={'flex'} alignItems={'center'} gap={1}>
          <Rating value={review.rating} size={'medium'} precision={0.1} readOnly />
        </Box>

        <Box sx={{ mt: 1 }}>
          <Box sx={{ borderRadius: 2, bgcolor: "#724E4135", padding: 1 }}>
            <Typography variant={'body2'}>{review.comment}</Typography>
          </Box>
          <Typography variant={'caption'}>
            {format(new Date(review.created_at), "dd MMM, yyyy")}
          </Typography>
        </Box>

      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewReviewDialog;
